import type { CodeNetworkEdge, CodeNetworkNode, SqlRow } from './codeNetworkStore.types';

export function quoteSqlIdentifier(identifier: string): string {
    if (!/^[A-Za-z0-9_ ]+$/.test(identifier)) {
        throw new Error(`Invalid SQL identifier: ${identifier}`);
    }
    return `[${identifier}]`;
}

export function getRowValue(row: SqlRow, field: string): unknown {
    if (field in row) {
        return row[field];
    }

    const key = Object.keys(row).find(x => x.toLowerCase() === field.toLowerCase());
    return key ? row[key] : undefined;
}

export function toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const num = Number(value);
    return Number.isFinite(num) ? num : null;
}

export function toStringOrNull(value: unknown): string | null {
    if (value === null || value === undefined) {
        return null;
    }

    const text = String(value);
    return text.length > 0 ? text : null;
}

export function toBool(value: unknown): boolean {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'number') {
        return value !== 0;
    }
    if (typeof value === 'string') {
        const lower = value.toLowerCase();
        return lower === 'true' || lower === '1';
    }
    return false;
}

export function nodeKey(library: string, id: number): string {
    return `${library}::${id}`;
}

export function edgeKey(from: string, to: string, attribute: string): string {
    return `${from}|${to}|${attribute}`;
}

export function mergeNode(incoming: CodeNetworkNode, existing?: CodeNetworkNode): CodeNetworkNode {
    if (!existing) {
        return incoming;
    }

    return {
        ...existing,
        name: incoming.name || existing.name,
        description: incoming.description || existing.description,
        isValid: incoming.isValid,
        identity: incoming.identity ?? existing.identity,
        scope: incoming.scope ?? existing.scope,
        iri: incoming.iri ?? existing.iri
    };
}

export function normalizeNodeFromRow(row: SqlRow, fallbackLibrary?: string): CodeNetworkNode | null {
    const id = toNumber(getRowValue(row, 'Id'));
    const library = toStringOrNull(getRowValue(row, 'Library')) ?? fallbackLibrary ?? null;

    if (id === null || !library) {
        return null;
    }

    const name = toStringOrNull(getRowValue(row, 'Name')) ?? `Code ${id}`;
    const description = toStringOrNull(getRowValue(row, 'Description')) ?? '';
    const isValid = toBool(getRowValue(row, 'IsValid'));
    const identity = toStringOrNull(getRowValue(row, 'Identity'));
    const scope = toStringOrNull(getRowValue(row, 'Scope'));
    const iri = toStringOrNull(getRowValue(row, 'CommonLibraryIRI'));

    return {
        key: nodeKey(library, id),
        library,
        id,
        name,
        description,
        isValid,
        identity,
        scope,
        iri
    };
}

export function parseCodeRefTarget(dataType: string | null): string | null {
    if (!dataType) {
        return null;
    }

    const prefix = 'CodeRef:';
    if (!dataType.toLowerCase().startsWith(prefix.toLowerCase())) {
        return null;
    }

    const target = dataType.substring(prefix.length).trim();
    return target.length > 0 ? target : null;
}

function hashString(value: string): number {
    let hash = 0;
    for (let i = 0; i < value.length; i += 1) {
        hash = (hash << 5) - hash + value.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
}

export function createLibraryColor(library: string): string {
    const hue = hashString(library) % 360;
    return `hsl(${hue}, 62%, 58%)`;
}

export function createLibraryMutedColor(library: string): string {
    const hue = hashString(library) % 360;
    return `hsl(${hue}, 38%, 76%)`;
}

export function cloneNodeMap(nodes: Record<string, CodeNetworkNode>): Record<string, CodeNetworkNode> {
    return Object.fromEntries(Object.entries(nodes).map(([key, node]) => [key, { ...node }]));
}

export function cloneEdgeMap(edges: Record<string, CodeNetworkEdge>): Record<string, CodeNetworkEdge> {
    return Object.fromEntries(Object.entries(edges).map(([key, edge]) => [key, { ...edge }]));
}
